import { formatCents } from '@/libs/utils/format-money';

/**
 * How the companion writes an amount and a date, in one place.
 *
 * The cards and the "why" panel describe the same evidence, and the panel that
 * explains a card must not describe it in different words. That invariant was
 * stated in comments while every card kept a private formatter, so it held only
 * as long as someone remembered it. Here it is the same function or nothing.
 */

/** An amount stays in the currency it was earned in, never rounded to cents. */
export const formatCompanionMoney = (
  money: { amountCents: number; currencyCode: string },
  language: string
): string =>
  formatCents(money.amountCents, money.currencyCode, language, {
    maximumFractionDigits: 0,
    // An unknown currency code is upstream data, not a reason to break a card.
    fallback: (amount, currency) => `${Math.round(amount)} ${currency}`,
  });

/** The day and the month alone, for a publication the brand saw this week. */
export const COMPANION_DAY_MONTH: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'long',
};

/**
 * A stored instant as a date the reader can check. An unparseable value is
 * shown as it was stored rather than as "Invalid Date": the card is evidence,
 * and evidence nobody can read is still worth more than evidence that lies.
 */
export const formatCompanionDate = (
  iso: string,
  language: string,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium' }
): string => {
  const value = Date.parse(iso);
  if (!Number.isFinite(value)) return iso;
  return new Intl.DateTimeFormat(language, options).format(new Date(value));
};

/**
 * The same reading for a moment that may not exist at all, with the time of
 * day: nothing to show returns null so the caller drops the whole line rather
 * than printing a placeholder.
 */
export const formatCompanionDateTime = (
  iso: string | null | undefined,
  language: string
): string | null => {
  if (!iso) return null;
  const value = Date.parse(iso);
  if (!Number.isFinite(value)) return null;
  return new Intl.DateTimeFormat(language, { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(value)
  );
};
